/**
 * AttachSourceToAgentsDialog
 * Generic dialog for attaching a source (Q&A / Text Source) to multiple agents.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Typography,
  CircularProgress,
  Box,
  Alert,
  Chip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import adminApi from '@/services/admin.api';

interface Agent {
  _id: string;
  name: string;
  topics?: string[];
}

interface AttachSourceToAgentsDialogProps {
  open: boolean;
  sourceIds: string[]; // typically a single selected source id
  sourceLabel: string; // e.g. "Q&A" or "Text Source"
  sourceName: string;
  onClose: () => void;
  onSuccess: () => void;
  getAttachedAgents: (sourceId: string) => Promise<Agent[]>;
  attachSourcesToAgents: (sourceIds: string[], agentIds: string[]) => Promise<void>;
  detachAgentFromSource: (agentId: string, sourceId: string) => Promise<void>;
}

const AttachSourceToAgentsDialog: React.FC<AttachSourceToAgentsDialogProps> = ({
  open,
  sourceIds,
  sourceLabel,
  sourceName,
  onClose,
  onSuccess,
  getAttachedAgents,
  attachSourcesToAgents,
  detachAgentFromSource,
}) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [attachedAgentIds, setAttachedAgentIds] = useState<string[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingAttached, setLoadingAttached] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [error, setError] = useState('');

  const firstSourceId = useMemo(() => sourceIds[0] || '', [sourceIds.join(',')]);

  const loadAgents = useCallback(async () => {
    try {
      setLoadingAgents(true);
      setError('');
      const response = await adminApi.getAgents();
      setAgents(response.data || []);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load agents';
      setError(errorMessage);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  const loadAttachedAgents = useCallback(async () => {
    if (!firstSourceId) {
      setAttachedAgentIds([]);
      return;
    }

    try {
      setLoadingAttached(true);
      const attached = await getAttachedAgents(firstSourceId);
      setAttachedAgentIds(attached.map((a) => a._id));
    } catch (err) {
      // Don't hard fail the whole dialog; just treat as none attached.
      setAttachedAgentIds([]);
      setError('');
    } finally {
      setLoadingAttached(false);
    }
  }, [firstSourceId, getAttachedAgents]);

  useEffect(() => {
    if (!open) return;
    if (sourceIds.length === 0) return;

    setSelectedAgents([]);
    setAttachedAgentIds([]);
    setError('');

    loadAgents();
    loadAttachedAgents();
  }, [open, sourceIds.length, loadAgents, loadAttachedAgents]);

  const handleToggleAgent = useCallback((agentId: string) => {
    setSelectedAgents((prev) =>
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    );
  }, []);

  const handleDetachAgent = useCallback(
    async (agentId: string) => {
      if (!firstSourceId) return;
      try {
        setError('');
        await detachAgentFromSource(agentId, firstSourceId);
        await loadAttachedAgents();
        onSuccess();
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message ||
          'Failed to detach agent';
        setError(errorMessage);
      }
    },
    [firstSourceId, detachAgentFromSource, loadAttachedAgents, onSuccess]
  );

  const handleAttach = useCallback(async () => {
    if (selectedAgents.length === 0) {
      setError('Please select at least one agent');
      return;
    }
    if (sourceIds.length === 0) {
      setError('No sources selected');
      return;
    }

    try {
      setAttaching(true);
      setError('');
      await attachSourcesToAgents(sourceIds, selectedAgents);
      setSelectedAgents([]);
      await loadAttachedAgents();
      onSuccess();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        `Failed to attach ${sourceLabel}`;
      setError(errorMessage);
    } finally {
      setAttaching(false);
    }
  }, [
    selectedAgents,
    sourceIds,
    attachSourcesToAgents,
    loadAttachedAgents,
    onSuccess,
    sourceLabel,
  ]);

  const handleClose = useCallback(() => {
    if (!attaching) {
      setSelectedAgents([]);
      setAttachedAgentIds([]);
      setError('');
      onClose();
    }
  }, [attaching, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attach {sourceLabel} to Agents</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select agents to attach &quot;{sourceName}&quot; to:
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loadingAttached ? (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading attached agents...
            </Typography>
          </Box>
        ) : attachedAgentIds.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Already attached to:
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {agents
                .filter((agent) => attachedAgentIds.includes(agent._id))
                .map((agent) => (
                  <Chip
                    key={agent._id}
                    label={agent.name}
                    size="small"
                    onDelete={() => handleDetachAgent(agent._id)}
                    deleteIcon={<CloseIcon />}
                    color="primary"
                    variant="outlined"
                  />
                ))}
            </Box>
          </Box>
        ) : null}

        {loadingAgents ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : agents.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" p={3}>
            No agents available
          </Typography>
        ) : (
          <List>
            {agents
              .filter((agent) => !attachedAgentIds.includes(agent._id))
              .map((agent) => (
                <ListItem
                  key={agent._id}
                  button
                  onClick={() => handleToggleAgent(agent._id)}
                  sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <Checkbox
                    checked={selectedAgents.includes(agent._id)}
                    onChange={() => handleToggleAgent(agent._id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <ListItemText
                    primary={agent.name}
                    secondary={agent.topics?.length ? agent.topics.join(', ') : 'No topics'}
                  />
                </ListItem>
              ))}
          </List>
        )}

        {selectedAgents.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {selectedAgents.length} agent(s) selected
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={attaching}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAttach}
          disabled={attaching || selectedAgents.length === 0 || loadingAgents}
        >
          {attaching ? 'Attaching...' : 'Attach'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachSourceToAgentsDialog;
